'use client';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Ref,
} from 'react';
import { Button } from '@curio/ui';

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(id: string): void;
  reset(id: string): void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}
let loading: Promise<TurnstileApi> | undefined;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    const fail = () => {
      clearTimeout(timer);
      script.remove();
      loading = undefined;
      reject(
        Error(
          'The security check could not load. Check your connection and retry.',
        ),
      );
    };
    const timer = setTimeout(fail, 15000);
    script.onerror = fail;
    script.onload = () => {
      clearTimeout(timer);
      if (window.turnstile) resolve(window.turnstile);
      else fail();
    };
    document.head.appendChild(script);
  });
  return loading;
}
export interface SecurityCheck {
  token(): Promise<string>;
  reset(): void;
}
export function Turnstile({
  siteKey,
  action,
  ref,
}: {
  siteKey: string | null;
  action: 'signup' | 'social' | 'email';
  ref: Ref<SecurityCheck>;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const token = useRef<{ value: string; at: number } | null>(null);
  const pending = useRef<{
    resolve: (value: string) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const failure = useRef<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  const finish = (error: Error | null, value = '') => {
    if (!pending.current) return;
    clearTimeout(pending.current.timer);
    if (error) pending.current.reject(error);
    else pending.current.resolve(value);
    pending.current = null;
  };
  useEffect(() => {
    if (!siteKey) return;
    let active = true;
    token.current = null;
    failure.current = null;
    const fail = (error: Error) => {
      if (!active) return;
      failure.current = error;
      token.current = null;
      if (pending.current) {
        setStatus(error.message);
        setFailed(true);
      }
      finish(error);
    };
    void loadTurnstile()
      .then((api) => {
        if (!active || !container.current) return;
        widget.current = api.render(container.current, {
          sitekey: siteKey,
          action,
          appearance: 'interaction-only',
          theme: 'auto',
          size: 'flexible',
          'response-field': false,
          callback: (value: string) => {
            token.current = { value, at: Date.now() };
            failure.current = null;
            setStatus('');
            setFailed(false);
            finish(null, value);
          },
          'expired-callback': () => {
            token.current = null;
          },
          'error-callback': () => {
            fail(Error('The security check was unsuccessful. Please retry.'));
          },
          'timeout-callback': () => {
            fail(Error('The security check timed out. Please retry.'));
          },
          'before-interactive-callback': () => {
            setStatus('Complete the quick security check below to continue.');
          },
        });
      })
      .catch((error) => fail(error as Error));
    return () => {
      active = false;
      finish(Error('The security check changed. Please try again.'));
      if (widget.current !== null) window.turnstile?.remove(widget.current);
      widget.current = null;
      token.current = null;
    };
  }, [siteKey, action, attempt]);
  useImperativeHandle(
    ref,
    () => ({
      token: async () => {
        if (!siteKey) return '';
        if (failure.current) {
          setStatus(failure.current.message);
          setFailed(true);
          throw failure.current;
        }
        if (token.current && Date.now() - token.current.at < 240000)
          return token.current.value;
        if (pending.current)
          throw Error('A security check is already in progress.');
        setFailed(false);
        setStatus('Checking your connection…');
        return new Promise<string>((resolve, reject) => {
          pending.current = {
            resolve,
            reject,
            timer: setTimeout(() => {
              const error = Error(
                'The security check is taking longer than expected. Please retry.',
              );
              setStatus(error.message);
              setFailed(true);
              finish(error);
            }, 30000),
          };
          if (token.current && widget.current !== null) {
            token.current = null;
            window.turnstile?.reset(widget.current);
          }
        });
      },
      reset: () => {
        token.current = null;
        if (widget.current !== null) window.turnstile?.reset(widget.current);
      },
    }),
    [siteKey],
  );
  if (!siteKey) return null;
  return (
    <div>
      {status && (
        <p className="caption secondary" role="status">
          {status}
        </p>
      )}
      <div ref={container} role="group" aria-label="Security verification" />
      {failed && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setStatus('Retrying the security check…');
            setFailed(false);
            setAttempt((n) => n + 1);
          }}
        >
          Retry security check
        </Button>
      )}
      <p className="caption secondary">
        Protected by Cloudflare.{' '}
        <a
          href="https://www.cloudflare.com/privacypolicy/"
          style={{ textDecoration: 'underline' }}
          target="_blank"
          rel="noreferrer"
        >
          Privacy
        </a>{' '}
        ·{' '}
        <a
          href="https://www.cloudflare.com/website-terms/"
          style={{ textDecoration: 'underline' }}
          target="_blank"
          rel="noreferrer"
        >
          Terms
        </a>
      </p>
    </div>
  );
}
